import { type Gradient, isGradient, type Paint, paintToCss } from "@cva/shared";
import { Link2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/utils";
import { type BrandLink, ColorEditor, useBrandColorName } from "./ColorPicker";
import { useEditorApi } from "./store";
import { NumberField, Popover, PopoverContent, PopoverTrigger, Segmented, Slider } from "./ui";

const CHECKER = "repeating-conic-gradient(#e4e4e7 0% 25%, #fff 0% 50%) 50% / 10px 10px";

const GRADIENT_PRESETS: Gradient[] = [
  {
    type: "linear",
    angle: 135,
    stops: [
      { offset: 0, color: "#2457eb" },
      { offset: 1, color: "#9256f5" },
    ],
  },
  {
    type: "linear",
    angle: 90,
    stops: [
      { offset: 0, color: "#f97316" },
      { offset: 1, color: "#facc15" },
    ],
  },
  {
    type: "linear",
    angle: 180,
    stops: [
      { offset: 0, color: "#0ea5e9" },
      { offset: 1, color: "#1e3a8a" },
    ],
  },
  {
    type: "linear",
    angle: 45,
    stops: [
      { offset: 0, color: "#22c55e" },
      { offset: 1, color: "#14b8a6" },
    ],
  },
  {
    type: "radial",
    stops: [
      { offset: 0, color: "#ffffff" },
      { offset: 1, color: "#a1a1aa" },
    ],
  },
  {
    type: "linear",
    angle: 180,
    stops: [
      { offset: 0, color: "#111111" },
      { offset: 1, color: "#525252" },
    ],
  },
];

function GradientEditor({
  value,
  onChange,
}: {
  value: Gradient;
  onChange: (gradient: Gradient) => void;
}) {
  const [active, setActive] = useState(0);
  const stops = value.stops;
  const current = stops[Math.min(active, stops.length - 1)]!;
  const setStop = (index: number, patch: Partial<Gradient["stops"][number]>) =>
    onChange({
      ...value,
      stops: stops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop)),
    });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Segmented
          value={value.type}
          onChange={(type) =>
            onChange(type === "linear" ? { type, angle: 90, stops } : { type: "radial", stops })
          }
          options={[
            { value: "linear", label: "Linear", title: "Linearer Verlauf" },
            { value: "radial", label: "Radial", title: "Radialer Verlauf" },
          ]}
        />
        {value.type === "linear" ? (
          <NumberField
            label="∠"
            value={value.angle}
            unit="°"
            onChange={(angle) => onChange({ ...value, angle: ((angle % 360) + 360) % 360 })}
            className="w-24"
          />
        ) : null}
      </div>
      <div
        className="relative h-6 rounded-md ring-1 ring-black/10"
        style={{
          background: `${paintToCss({ ...value, type: "linear", angle: 90 } as Gradient)}, ${CHECKER}`,
        }}
      >
        {stops.map((stop, index) => (
          <button
            // biome-ignore lint/suspicious/noArrayIndexKey: stops are positional
            key={index}
            type="button"
            onClick={() => setActive(index)}
            className={cn(
              "absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 shadow",
              index === active ? "border-brand-600" : "border-white",
            )}
            style={{ left: `${stop.offset * 100}%`, background: stop.color }}
            title={`Farbstopp ${index + 1}`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">Position</span>
        <Slider
          value={current.offset * 100}
          min={0}
          max={100}
          onChange={(v) => setStop(active, { offset: v / 100 })}
        />
        <button
          type="button"
          title="Farbstopp hinzufügen"
          disabled={stops.length >= 8}
          className="inline-flex size-7 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 disabled:opacity-40"
          onClick={() => {
            const sorted = [...stops].sort((a, b) => a.offset - b.offset);
            const next = [
              ...stops,
              { offset: 0.5, color: sorted[Math.floor(sorted.length / 2)]?.color ?? "#ffffff" },
            ];
            onChange({ ...value, stops: next });
            setActive(next.length - 1);
          }}
        >
          <Plus className="size-4" />
        </button>
        <button
          type="button"
          title="Farbstopp entfernen"
          disabled={stops.length <= 2}
          className="inline-flex size-7 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 disabled:opacity-40"
          onClick={() => {
            onChange({ ...value, stops: stops.filter((_, i) => i !== active) });
            setActive(0);
          }}
        >
          <Trash2 className="size-4" />
        </button>
      </div>
      <ColorEditor value={current.color} onChange={(color) => setStop(active, { color })} />
    </div>
  );
}

/** Fill selection: solid colour or gradient. */
export function FillButton({
  value,
  onChange,
  label = "Füllung",
  brandLink,
}: {
  value: Paint;
  onChange: (paint: Paint) => void;
  label?: string;
  /** Solid fills can be linked to a brand colour. */
  brandLink?: BrandLink;
}) {
  const store = useEditorApi();
  const gradient = isGradient(value);
  const brandName = useBrandColorName(gradient ? undefined : brandLink?.token);
  return (
    <Popover
      onOpenChange={(open) => {
        if (open) store.getState().beginGesture({ live: true });
        else store.getState().endGesture();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-white pr-2 pl-1 text-xs hover:bg-zinc-50"
        >
          <span
            className="size-6 overflow-hidden rounded ring-1 ring-black/10 ring-inset"
            style={{ background: CHECKER }}
          >
            <span className="block size-full" style={{ background: paintToCss(value) }} />
          </span>
          {brandName ? (
            <span
              className="inline-flex items-center gap-1 text-brand-700"
              title="Mit der Marke verknüpft"
            >
              <Link2 className="size-3" />
              {brandName}
            </span>
          ) : (
            <span className="text-zinc-600">
              {gradient ? (value.type === "linear" ? "Verlauf" : "Radial") : value.toUpperCase()}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <Segmented
          className="mb-3 w-full"
          value={gradient ? "gradient" : "solid"}
          onChange={(mode) => {
            if (mode === "solid" && gradient) onChange(value.stops[0]?.color ?? "#ffffff");
            if (mode === "gradient" && !gradient) {
              onChange({
                type: "linear",
                angle: 90,
                stops: [
                  { offset: 0, color: value },
                  { offset: 1, color: "#ffffff" },
                ],
              });
            }
          }}
          options={[
            { value: "solid", label: "Farbe", title: "Einfarbig" },
            { value: "gradient", label: "Verlauf", title: "Farbverlauf" },
          ]}
        />
        {gradient ? (
          <>
            <div className="mb-3 flex gap-1.5">
              {GRADIENT_PRESETS.map((preset, index) => (
                <button
                  // biome-ignore lint/suspicious/noArrayIndexKey: static list
                  key={index}
                  type="button"
                  title="Vorlage"
                  className="size-7 rounded-md ring-1 ring-black/10"
                  style={{ background: paintToCss(preset) }}
                  onClick={() => onChange(preset)}
                />
              ))}
            </div>
            <GradientEditor value={value} onChange={onChange} />
          </>
        ) : (
          <ColorEditor value={value} onChange={onChange} brandLink={brandLink} />
        )}
      </PopoverContent>
    </Popover>
  );
}
